/**
 * RegistrarSaldoFavorDialog — la agencia transfirió de más.
 *
 * El caso real: AGENCIA JBA mandó 690.000 por una reserva cuyo neto era
 * 480.000. Esos 210.000 entraron al banco y no estaban en ninguna parte. Acá
 * quedan registrados como saldo a favor de la agencia, que se aplica a su
 * próxima reserva.
 *
 * No toca el saldo de esta reserva —ya está cuadrada—: cambia la posición de
 * la agencia con Entretur.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, MenuItem, Alert, Stack,
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

const METODOS = [
  { v: 'transferencia', label: 'Transferencia' },
  { v: 'nequi', label: 'Nequi' },
  { v: 'efectivo', label: 'Efectivo' },
  { v: 'tarjeta', label: 'Tarjeta / datáfono' },
  { v: 'bold_link', label: 'Link Bold' },
];

const hoyISO = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

export default function RegistrarSaldoFavorDialog({
  open, onClose, reserva, onHecho, onToast,
}) {
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('transferencia');
  const [cuentaId, setCuentaId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [fecha, setFecha] = useState(hoyISO());
  const [motivo, setMotivo] = useState('');
  const [cuentas, setCuentas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMonto(''); setMetodo('transferencia'); setCuentaId('');
    setReferencia(''); setFecha(hoyISO()); setMotivo(''); setError(null);
    fetch(`${API_BASE}/turismo/correcciones/cuentas-destino?empresa_id=${reserva?.empresa_id}`,
          { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : []).then(setCuentas).catch(() => setCuentas([]));
  }, [open, reserva?.empresa_id]);

  const montoNum = Number(String(monto).replace(/[^\d]/g, '')) || 0;
  const puedeGuardar = montoNum > 0 && !!cuentaId && !guardando;

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/correcciones/reservas/${reserva.id}/saldo-a-favor`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            monto: montoNum,
            metodo_pago: metodo,
            cuenta_destino_id: Number(cuentaId),
            referencia: referencia.trim() || null,
            fecha_pago: fecha || null,
            motivo: motivo.trim() || null,
          }),
        });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail?.message || d.detail || 'No se pudo registrar');
      onToast?.success?.(
        `${formatPrecio(d.monto, reserva.moneda)} quedan a favor de ${d.agencia_nombre}. `
        + `Ahora tiene ${formatPrecio(d.saldo_a_favor_despues, reserva.moneda)}.`);
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo registrar');
    }
    setGuardando(false);
  };

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Registrar saldo a favor
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · {reserva?.agencia_nombre || 'la agencia'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#ecfeff', border: '1px solid #a5f3fc' }}>
            <Typography sx={{ fontSize: 12, color: '#155e75' }}>
              Lo que entró de más. No cambia el saldo de esta reserva: queda a favor de la
              agencia para aplicarlo a una reserva futura.
            </Typography>
          </Box>

          <TextField label="Cuánto entró de más" size="small" value={monto}
                     onChange={(e) => setMonto(e.target.value)}
                     helperText="La diferencia entre lo que transfirieron y lo que debían" />

          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField select label="Cómo entró" size="small" value={metodo}
                       onChange={(e) => setMetodo(e.target.value)}>
              {METODOS.map(m => <MenuItem key={m.v} value={m.v}>{m.label}</MenuItem>)}
            </TextField>
            <TextField select label="A qué cuenta" size="small" value={cuentaId}
                       onChange={(e) => setCuentaId(e.target.value)}>
              {cuentas.map(c => <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>)}
            </TextField>
          </Box>

          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField label="Referencia" size="small" value={referencia}
                       onChange={(e) => setReferencia(e.target.value)}
                       placeholder="Número del comprobante" />
            <TextField label="Fecha del pago" type="date" size="small" value={fecha}
                       onChange={(e) => setFecha(e.target.value)}
                       InputLabelProps={{ shrink: true }} />
          </Box>

          <TextField label="Nota (opcional)" size="small" multiline minRows={2}
                     value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="La transferencia del 20-sep fue por 690.000 y el neto era 480.000" />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" startIcon={<VolunteerActivismIcon />}
                disabled={!puedeGuardar} onClick={guardar}
                sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#0891b2',
                      '&:hover': { bgcolor: '#0e7490' } }}>
          {guardando ? 'Registrando…' : 'Registrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
